import { automationOverrides } from './human-default.js';
export function validateGraph(g) {
    const issues = [];
    const err = (code, stepKey, message) => issues.push({ severity: 'error', code, stepKey, message });
    const warn = (code, stepKey, message) => issues.push({ severity: 'warning', code, stepKey, message });
    const outbound = new Map();
    const inbound = new Map();
    for (const s of g.steps) {
        outbound.set(s.key, []);
        inbound.set(s.key, []);
    }
    for (const t of g.transitions) {
        // Dangling references are a schema error (ProcessGraphSchema); skip them here.
        outbound.get(t.from)?.push(t);
        inbound.get(t.to)?.push(t);
        if (t.from === t.to)
            err('self_loop', t.from, `"${t.from}" loops directly to itself.`);
    }
    const starts = g.steps.filter((s) => s.stepType === 'start');
    const ends = g.steps.filter((s) => s.stepType === 'end');
    if (starts.length !== 1) {
        err('start_count', null, `A process needs exactly one start step; found ${starts.length}.`);
    }
    if (ends.length === 0) {
        err('no_end', null, 'A process needs at least one end step.');
    }
    // Invariant 2: everything is reachable from start.
    if (starts.length === 1) {
        const reached = walk(starts[0].key, (k) => outbound.get(k)?.map((t) => t.to) ?? []);
        for (const s of g.steps) {
            if (!reached.has(s.key))
                err('unreachable', s.key, `"${s.name}" cannot be reached from the start.`);
        }
    }
    // Invariant 3 and end-node hygiene.
    for (const s of g.steps) {
        const outs = outbound.get(s.key) ?? [];
        if (s.stepType === 'end') {
            if (outs.length > 0)
                err('end_has_outbound', s.key, `End step "${s.name}" must not lead anywhere.`);
        }
        else if (outs.length === 0) {
            err('dead_end', s.key, `"${s.name}" has no next step and is not an end.`);
        }
    }
    // Invariant 4: decisions are fully specified.
    for (const s of g.steps.filter((s) => s.stepType === 'decision')) {
        const outs = outbound.get(s.key) ?? [];
        if (outs.length < 2) {
            err('decision_branches', s.key, `Decision "${s.name}" needs two or more outcomes; it has ${outs.length}.`);
        }
        const unlabeled = outs.filter((t) => t.condition === null || t.condition.trim() === '');
        if (unlabeled.length > 0) {
            err('decision_unlabeled', s.key, `Decision "${s.name}" has ${unlabeled.length} outcome(s) without a label.`);
        }
        const defaults = outs.filter((t) => t.isDefault);
        if (outs.length >= 2 && defaults.length !== 1) {
            err('decision_default', s.key, `Decision "${s.name}" must have exactly one default outcome; it has ${defaults.length}.`);
        }
    }
    // Invariant 6: every step can reach an end. Subsumes "cycle with no exit".
    if (ends.length > 0) {
        const reachesEnd = new Set();
        for (const e of ends) {
            for (const k of walk(e.key, (k) => inbound.get(k)?.map((t) => t.from) ?? []))
                reachesEnd.add(k);
        }
        for (const s of g.steps) {
            if (!reachesEnd.has(s.key)) {
                err('no_path_to_end', s.key, `"${s.name}" can never reach an end step — a loop with no exit.`);
            }
        }
    }
    // Invariant 7 (D-010): a performer outside the default on a flagged step needs a stated reason.
    for (const s of automationOverrides(g)) {
        if (s.overrideReason === null || s.overrideReason.trim() === '') {
            err('override_reason_required', s.key, `"${s.name}" is performed by ${s.performedBy} despite its flags. State why before submitting.`);
        }
    }
    // Soft checks.
    for (const s of g.steps) {
        if (s.stepType === 'start' || s.stepType === 'end')
            continue;
        const accountable = s.raci.filter((r) => r.letter === 'A').length;
        if (accountable !== 1) {
            warn('raci_accountable', s.key, `"${s.name}" should have exactly one Accountable role; it has ${accountable}.`);
        }
        if (s.stepType === 'wait' && (s.touchMinutes ?? 0) > 0) {
            warn('wait_has_touch_time', s.key, `Wait step "${s.name}" records work time; is it really a wait?`);
        }
    }
    return issues;
}
export const hasBlockingIssues = (issues) => issues.some((i) => i.severity === 'error');
/** Breadth-first walk. Returns every key reachable from `from`, including `from`. */
function walk(from, next) {
    const seen = new Set([from]);
    const queue = [from];
    while (queue.length) {
        const k = queue.shift();
        for (const n of next(k)) {
            if (!seen.has(n)) {
                seen.add(n);
                queue.push(n);
            }
        }
    }
    return seen;
}
//# sourceMappingURL=validate-graph.js.map